import { useMemo } from 'react'
import storage from '@/utils/storage'
import type { LineOption } from '@/utils/storage'

type LineElement = { displayName?: string | null } | undefined
type LineElements = Record<string, LineElement>
type AvailableLinesSpace = {
    id: string
    assistants: Array<{
        metadata?: unknown
    }>
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null

const isLineElement = (value: unknown): value is LineElement =>
    value === undefined
    || (isRecord(value)
        && (value.displayName === undefined
            || value.displayName === null
            || typeof value.displayName === 'string'))

const isLineElements = (value: unknown): value is LineElements =>
    isRecord(value) && Object.values(value).every(isLineElement)

const getLineElements = (metadata: unknown): LineElements | undefined => {
    if (!isRecord(metadata) || !isRecord(metadata.filter)) return undefined
    if (!isRecord(metadata.filter.line)) return undefined

    const lineElements = metadata.filter.line.elements
    return isLineElements(lineElements) ? lineElements : undefined
}

const useAvailableLines = (spaces: AvailableLinesSpace[], getActiveSpace: () => AvailableLinesSpace | undefined): LineOption[] =>
    useMemo(() => {
        const space = getActiveSpace()
        for (const assistant of space?.assistants ?? []) {
            const lineElements = getLineElements(assistant.metadata)
            if (lineElements && Object.keys(lineElements).length > 0) {
                return Object.entries(lineElements).map(([key, val]) => ({
                    value: key,
                    label: val?.displayName ?? key,
                })) 
            }   
        }   
        return space ? storage.getAvailableLines(space.id) : []
        // getActiveSpace is created again on every render: the lines are read again when the spaces change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spaces])

export default useAvailableLines